import React, { useEffect, useState } from "react";
import { App, Form, List, Modal, Radio, Select, Skeleton } from "antd5"; //TODO: refactor to react hook form

import UserInitials from "components/comments/UserInitials";
import UserName from "components/users/UserName";
import { useBuyerListPermissions } from "lib/hooks/api/buyer_lists/useBuyerListPermissions";
import {
  AssignedRole,
  useUpdateBuyerListPermissions,
} from "lib/hooks/api/buyer_lists/useUpdateBuyerListPermissions";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useUsers } from "lib/hooks/api/useUsers";
import { BuyerListRoleNameEnum } from "lib/types/graphQLEnums";

import css from "./BuyerListPermissionsModal.module.scss";

const roles = [
  { value: "buyer_list_viewer", label: "can view" },
  { value: "buyer_list_editor", label: "can edit" },
  { value: "buyer_list_admin", label: "admin" },
];

export type BuyerListPermissionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  list: { id: string; name: string; sharingType: string };
};

function BuyerListPermissionsModal({ isOpen, onClose, list }: BuyerListPermissionsModalProps) {
  const { message } = App.useApp();
  const { data: currentUser } = useCurrentUser();
  const { isLoading, mutate } = useUpdateBuyerListPermissions({
    onSuccess: () => {
      void message.success("Buyer list updated successfully!");
      onClose();
    },
    onError() {
      void message.error("Failed to update buyer list");
    },
  });
  const { data: permissions, isLoading: isLoadingPermissions } = useBuyerListPermissions(list.id);
  // includeInactive: true so previously-shared but now-deactivated users still resolve.
  // The picker filters deactivated users out for new shares via the visibleUsers list below.
  const { data: users } = useUsers({ includeInactive: true });
  const [usersWithRoles, setUsersWithRoles] = useState<AssignedRole[]>([]);
  const [selectedUserGuids, setSelectedUserGuids] = useState<string[]>([]);

  useEffect(() => {
    if (permissions && users) {
      const defaultSelectedGuids: string[] = [];
      const userRoles: AssignedRole[] = [];
      permissions.forEach((perm) => {
        if (perm.accessorType === "User") {
          const user = users.find((u) => u.guid === perm.accessorId);

          if (user) {
            defaultSelectedGuids.push(user.guid);
            userRoles.push({
              accessorId: perm.accessorId,
              roleName: perm.roleName,
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              active: user.active,
            });
          }
        }
      });
      setSelectedUserGuids(defaultSelectedGuids);
      setUsersWithRoles(userRoles);
    }
  }, [permissions, users]);

  const onSubmit = () => {
    mutate({
      id: list.id,
      updates: usersWithRoles.map((u) => ({
        accessorId: u.accessorId,
        ...(u.roleName ? { roleName: u.roleName as BuyerListRoleNameEnum } : {}),
      })),
    });
  };

  function onUserSelect(guid: string) {
    const user = users?.find((u) => u.guid === guid);
    if (!user) return;
    const index = usersWithRoles.findIndex((perm) => perm.accessorId === guid);
    const updated = [...usersWithRoles];
    if (index > -1) {
      updated[index].roleName = "buyer_list_viewer";
    } else {
      updated.push({
        roleName: "buyer_list_viewer",
        accessorId: user.guid,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        active: user.active,
      });
    }
    setUsersWithRoles(updated);
  }

  function onUserDeselect(guid: string) {
    setUsersWithRoles(
      usersWithRoles.map((item) => (item.accessorId === guid ? { ...item, roleName: null } : item)),
    );
  }

  // Hide deactivated users from the picker — existing permissions referencing them
  // remain in usersWithRoles via the effect above, so historical shares aren't lost.
  const visibleUsers = users?.filter((user) => user.active !== false) ?? [];

  if (list.sharingType === "TEAM") {
    return (
      <Modal onOk={onClose} onCancel={onClose} title={`Share "${list.name}"`} open={isOpen}>
        <Form layout="vertical" hideRequiredMark>
          <Form.Item label="Shared with" help="Everyone can view and edit this list">
            <Radio.Group disabled value={list.sharingType}>
              <Radio value="PRIVATE">Private</Radio>
              <Radio value="TEAM">Team</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    );
  }

  return (
    <Modal
      title={`Share "${list.name}"`}
      open={isOpen}
      onCancel={onClose}
      okText={isLoading ? "Updating..." : "Update"}
      onOk={onSubmit}
      okButtonProps={{ disabled: isLoading || selectedUserGuids.length === 0 }}
      cancelButtonProps={{ disabled: isLoading }}
    >
      <h4>People with access</h4>
      {isLoadingPermissions ? (
        <Skeleton paragraph={false} />
      ) : (
        <>
          <Select
            showSearch
            mode="multiple"
            onSelect={(val: string) => onUserSelect(val)}
            onDeselect={(val: string) => onUserDeselect(val)}
            onChange={(val: string[]) => setSelectedUserGuids(val)}
            value={selectedUserGuids}
            className={css.userSelect}
          >
            {visibleUsers.map((user) => (
              <Select.Option
                key={user.guid}
                value={user.guid}
                disabled={currentUser?.guid === user.guid}
              >{`${user.firstName} ${user.lastName}`}</Select.Option>
            ))}
          </Select>
          <List className={css.roleList}>
            {usersWithRoles.map(
              (user, index) =>
                user.roleName && (
                  <List.Item
                    key={user.accessorId}
                    actions={[
                      <Select
                        value={user.roleName}
                        key={user.email}
                        onChange={(val: string) => {
                          const updated = [...usersWithRoles];
                          updated[index].roleName = val;
                          setUsersWithRoles(updated);
                        }}
                        size="small"
                        className={css.roleSelect}
                        disabled={currentUser?.guid === user.accessorId}
                      >
                        {roles.map((role) => (
                          <Select.Option key={role.value} value={role.value}>
                            {role.label}
                          </Select.Option>
                        ))}
                      </Select>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <UserInitials
                          type="small"
                          firstName={user.firstName}
                          lastName={user.lastName}
                        />
                      }
                      title={<UserName user={user} />}
                    />
                  </List.Item>
                ),
            )}
          </List>
        </>
      )}
    </Modal>
  );
}

export default BuyerListPermissionsModal;
